import { type DeliveryNotesAdminQueryParameters } from '~/data/delivery';

import {
  date4server,
  type DateRangeLabel,
  FILTER_DATE_RANGES,
  formatDate,
} from '~/utils';

type P = {
  readonly dateRangeLabel: DateRangeLabel;
  readonly filterConfig: DeliveryNotesAdminQueryParameters['filterConfig'];
  readonly searchString: string;
};

/**
 * Returns a full filter configuration by adding date range and search filters to a base filter configuration.
 *
 * @param params - Configuration parameters
 * @param params.dateRangeLabel - Label key for predefined date ranges (e.g., 'thisMonth', 'lastWeek')
 * @param params.filterConfig - Base filter configuration to extend
 * @param params.searchString - Search term to filter assets by
 * @returns Complete filter configuration with date range and search filters applied
 */
export const getFullFilterConfig = ({
  dateRangeLabel,
  filterConfig,
  searchString,
}: P) => {
  const isoStartDate = formatDate(
    FILTER_DATE_RANGES[dateRangeLabel]?.start,
    date4server,
  );

  const isoEndDate = formatDate(
    FILTER_DATE_RANGES[dateRangeLabel]?.end,
    date4server,
  );

  const fullFilterConfig = {
    ...filterConfig,
    filterGroups: [
      ...filterConfig.filterGroups,
      {
        filterAggr: 'and',
        filterClauses: [
          dateRangeLabel && {
            name: 'dln_date',
            comp: 'ge',
            value: isoStartDate,
          },
          dateRangeLabel && {
            name: 'dln_date',
            comp: 'le',
            value: isoEndDate,
          },
          searchString?.trim() && {
            caseSensitive: false,
            comp: 'contains',
            name: 'search',
            value: searchString.trim(),
          },
        ].filter(Boolean),
      },
    ].filter(Boolean),
  };

  return fullFilterConfig;
};
